import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type { Arrangement } from '@/types';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface ArrangementContextValue {
  arrangement: Arrangement | null;
  arrangements: Arrangement[];
  loading: boolean;
  setArrangement: (a: Arrangement) => Promise<{ error: string | null }>;
  clearArrangement: () => void;
  deleteArrangement: (id: string) => Promise<void>;
  setActiveArrangement: (id: string) => void;
  updateSettings: (id: string, patch: Record<string, unknown>) => Promise<void>;
  /** Only the owner may delete an arrangement; editors get everything else */
  isOwner: (a: Arrangement) => boolean;
}

// ── DB ↔ TypeScript mappers ───────────────────────────────────────────────────

import { generateSlug } from '@/lib/slug';
import { parseCustomVendorCategory, serializeCustomVendorCategory } from '@/lib/vendorCategories';
import { partnerGroupRenames, renameGuestGroupInDb, storedGroups } from '@/lib/guestGroups';
import { moveDefaultDays } from '@/hooks/useSchedule';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function fromDb(row: any): Arrangement {
  return {
    id: row.id,
    slug: row.slug ?? generateSlug(row.name, row.id),
    userId: row.user_id,
    name: row.name,
    eventType: row.event_type,
    date: row.date ?? undefined,
    hostName: row.host_name,
    partner1: row.partner1 ?? undefined,
    partner2: row.partner2 ?? undefined,
    guestCount: row.guest_count ?? undefined,
    location: row.location ?? undefined,
    budget: row.budget ?? undefined,
    modules: row.modules ?? [],
    customVendorCategories: (row.custom_vendor_categories ?? []).map(parseCustomVendorCategory),
    invitationTemplate: row.settings?.invitation_template ?? undefined,
    invitationCopy: row.settings?.invitation_copy ?? undefined,
    settings: row.settings ?? {},
    createdAt: row.created_at,
  };
}

function toDb(a: Arrangement, userId: string) {
  return {
    id: a.id,
    slug: a.slug,
    user_id: a.userId || userId,
    name: a.name,
    event_type: a.eventType,
    date: a.date ?? null,
    host_name: a.hostName,
    partner1: a.partner1 ?? null,
    partner2: a.partner2 ?? null,
    guest_count: a.guestCount ?? null,
    location: a.location ?? null,
    budget: a.budget ?? null,
    modules: a.modules,
    custom_vendor_categories: (a.customVendorCategories ?? []).map(serializeCustomVendorCategory),
  };
}

/** Dated events first by date, then undated ones by name */
function byDateThenName(a: Arrangement, b: Arrangement) {
  if (a.date && b.date) return a.date.localeCompare(b.date);
  if (a.date || b.date) return a.date ? -1 : 1;
  return a.name.localeCompare(b.name, 'nb');
}

function activeKey(userId: string) {
  return `dendagen_active_${userId}`;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ArrangementContext = createContext<ArrangementContextValue | null>(null);

export function ArrangementProvider({ children, userId }: { children: ReactNode; userId?: string }) {
  const [arrangements, setArrangements] = useState<Arrangement[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(() =>
    userId ? localStorage.getItem(activeKey(userId)) : null,
  );

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Links pending editor invitations for this email before the list is read
    await supabase.rpc('claim_memberships');
    const { data, error } = await supabase.from('arrangements').select('*').order('created_at', { ascending: true });

    if (!error && data) {
      const mapped = data.map(fromDb);
      setArrangements(mapped);
      // If stored activeId no longer exists, fall back to first
      setActiveId((prev) => {
        if (prev && mapped.find((a) => a.id === prev)) return prev;
        return mapped[0]?.id ?? null;
      });
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Persist active arrangement ID locally (UI state only)
  useEffect(() => {
    if (!userId) return;
    if (activeId) localStorage.setItem(activeKey(userId), activeId);
    else localStorage.removeItem(activeKey(userId));
  }, [activeId, userId]);

  const sortedArrangements = useMemo(() => [...arrangements].sort(byDateThenName), [arrangements]);

  const arrangement = activeId ? (arrangements.find((a) => a.id === activeId) ?? arrangements[0] ?? null) : null;

  async function setArrangement(a: Arrangement): Promise<{ error: string | null }> {
    if (!userId) return { error: 'Ikke innlogget' };
    const { error } = await supabase.from('arrangements').upsert(toDb(a, userId));
    if (error) return { error: error.message };
    const previous = arrangements.find((x) => x.id === a.id);
    // Before the state update, so an open program reloads with the moved days
    if (previous?.date && a.date && previous.date !== a.date) {
      const moved = await moveDefaultDays(a.id, a.eventType, previous.date, a.date);
      if (moved.error) toast.error('Kunne ikke flytte dagene i programmet');
    }
    setArrangements((prev) => {
      const exists = prev.find((x) => x.id === a.id);
      return exists ? prev.map((x) => (x.id === a.id ? a : x)) : [...prev, a];
    });
    setActiveId(a.id);
    if (previous) await propagatePartnerRenames(previous, a);
    if (previous?.date && !a.date) await cancelScheduledInvitations(a.id);
    return { error: null };
  }

  // Invitations can't be sent without a date, so a removed date cancels the queue
  async function cancelScheduledInvitations(arrangementId: string) {
    const { error } = await supabase
      .from('invitations')
      .update({ status: 'draft', scheduled_at: null })
      .eq('arrangement_id', arrangementId)
      .eq('status', 'scheduled');
    if (error) toast.error('Kunne ikke avbryte planlagte invitasjoner');
  }

  async function propagatePartnerRenames(previous: Arrangement, next: Arrangement) {
    const renames = partnerGroupRenames(previous, next);
    if (renames.length === 0) return;
    await Promise.all(renames.map(([from, to]) => renameGuestGroupInDb(next.id, from, to)));
    const stored = storedGroups(next);
    if (!stored) return;
    const renamed = stored.map((g) => renames.find(([from]) => from === g)?.[1] ?? g);
    await updateSettings(next.id, { guest_groups: renamed });
  }

  function clearArrangement() {
    setActiveId(null);
  }

  async function deleteArrangement(id: string) {
    if (!userId) return;
    const { error } = await supabase.from('arrangements').delete().eq('id', id);
    if (!error) {
      setArrangements((prev) => prev.filter((a) => a.id !== id));
      if (activeId === id) setActiveId(null);
    }
  }

  function setActiveArrangement(id: string) {
    setActiveId(id);
  }

  function isOwner(a: Arrangement) {
    return !!userId && a.userId === userId;
  }

  // Targeted update instead of the full-row upsert in setArrangement, so saving
  // an arrangement never depends on the settings column
  async function updateSettings(id: string, patch: Record<string, unknown>) {
    const previous = arrangements;
    const settings = { ...previous.find((a) => a.id === id)?.settings, ...patch };
    setArrangements((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              settings,
              invitationTemplate: (settings.invitation_template as string | undefined) ?? a.invitationTemplate,
              invitationCopy: (settings.invitation_copy as Arrangement['invitationCopy']) ?? a.invitationCopy,
            }
          : a,
      ),
    );
    const { error } = await supabase.from('arrangements').update({ settings }).eq('id', id);
    if (error) {
      setArrangements(previous);
      toast.error(`Kunne ikke lagre innstillingen: ${error.message}`);
    }
  }

  return (
    <ArrangementContext.Provider
      value={{
        arrangement,
        arrangements: sortedArrangements,
        loading,
        setArrangement,
        clearArrangement,
        deleteArrangement,
        setActiveArrangement,
        updateSettings,
        isOwner,
      }}
    >
      {children}
    </ArrangementContext.Provider>
  );
}

export function useArrangement() {
  const ctx = useContext(ArrangementContext);
  if (!ctx) throw new Error('useArrangement must be used inside ArrangementProvider');
  return ctx;
}
